import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';
import { ArrowDownRight, ArrowUpRight, BadgeAlert, Banknote, CalendarDays, ChevronDown, CircleDollarSign, ShieldAlert, Sparkles, TrendingUp, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function percent(value) {
  return `${Number(value || 0).toFixed(0)}%`;
}

function monthOptions() {
  return Array.from({ length: 12 }, (_, index) => {
    const month = index + 1;
    return { value: String(month), label: new Date(2025, index, 1).toLocaleString('en-US', { month: 'long' }) };
  });
}

function getCurrentMonth() {
  return String(new Date().getMonth() + 1);
}

function getCurrentYear() {
  return String(new Date().getFullYear());
}

function ToneBadge({ score, status }) {
  const tone = score >= 75 ? 'bg-emerald-100 text-emerald-800' : score >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
  return <Badge className={tone}>{status}</Badge>;
}

function HealthRing({ score }) {
  const safeScore = Math.max(0, Math.min(100, Number(score || 0)));
  const color = safeScore >= 75 ? '#059669' : safeScore >= 50 ? '#d97706' : '#dc2626';
  return (
    <div
      className="relative mx-auto h-32 w-32 rounded-full"
      style={{
        background: `conic-gradient(${color} ${safeScore}%, #e2e8f0 ${safeScore}% 100%)`,
      }}
    >
      <div className="absolute inset-3 rounded-full bg-white border border-slate-100 flex flex-col items-center justify-center text-center">
        <p className="text-3xl font-semibold text-slate-900">{safeScore}</p>
        <p className="text-xs text-slate-500">Health</p>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, subtext, icon: Icon, trend }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Icon size={18} className="text-emerald-700" />
          </div>
        </div>
        <p className="text-2xl font-bold text-slate-900 mono-nums">{value}</p>
        {subtext ? <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">{trend}{subtext}</p> : null}
      </CardContent>
    </Card>
  );
}

export default function BudgetOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(getCurrentMonth());
  const [year, setYear] = useState(getCurrentYear());
  const [branchId, setBranchId] = useState('all');
  const [currency, setCurrency] = useState('');

  const load = async (nextMonth = month, nextYear = year, nextBranch = branchId, nextCurrency = currency) => {
    setLoading(true);
    try {
      const params = { month: nextMonth, year: nextYear };
      if (nextBranch && nextBranch !== 'all') params.branch_id = nextBranch;
      if (nextCurrency) params.currency = nextCurrency;
      const res = await api.get('/budget/overview', { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load budget overview');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branchOptions = useMemo(() => {
    const items = Array.isArray(data?.branches) ? data.branches : [];
    return items;
  }, [data?.branches]);
  const activeBranchOptions = useMemo(
    () => branchOptions.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'),
    [branchOptions],
  );

  const currencyOptions = useMemo(() => {
    const items = Array.isArray(data?.filters?.currencies) ? data.filters.currencies : [];
    return items;
  }, [data?.filters?.currencies]);

  const handleFilterChange = (next = {}) => {
    const nextMonth = next.month ?? month;
    const nextYear = next.year ?? year;
    const nextBranch = next.branchId ?? branchId;
    const nextCurrency = next.currency ?? currency;
    setMonth(nextMonth);
    setYear(nextYear);
    setBranchId(nextBranch);
    setCurrency(nextCurrency);
    load(nextMonth, nextYear, nextBranch, nextCurrency);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <Sparkles size={22} className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget</h1>
              <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            {data?.message || 'This feature is available on premium packages only.'}
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              View Packages
            </Button>
            <Button variant="outline" className="border-slate-200" onClick={() => load()}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const health = data?.health || {};
  const insights = Array.isArray(data?.insights) ? data.insights : [];
  const advice = Array.isArray(data?.advice) ? data.advice : [];
  const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
  const branchModeSingle = activeBranchOptions.length <= 1 || !!data?.single_branch || data?.filters?.branch_mode === 'single';
  const mainCurrency = currency && currency !== 'all' ? currency : (data?.filters?.currency || user?.default_currency || 'UGX');
  const currentBalance = Number(summary.current_balance || 0);
  const openingBalance = Number(summary.opening_balance || 0);
  const netChange = Number(summary.net_change || 0);
  const balanceIsUp = currentBalance >= openingBalance;

  const chartData = Array.isArray(data?.charts?.income_vs_expenses) ? data.charts.income_vs_expenses : [];
  const categoryData = Array.isArray(data?.charts?.expense_categories) ? data.charts.expense_categories : [];
  const allocationData = Array.isArray(data?.charts?.allocation_breakdown) ? data.charts.allocation_breakdown : [];
  const cashFlowData = Array.isArray(data?.charts?.cash_flow_trend) ? data.charts.cash_flow_trend : [];
  const allocationMode = String(data?.allocation_mode || 'monthly_income').replace(/_/g, ' ');
  const COLORS = ['#059669', '#2563eb', '#7c3aed', '#f59e0b', '#ef4444', '#0ea5e9', '#ec4899', '#10b981'];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Budget Overview
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Monthly financial intelligence for your business and personal finances.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="min-w-[120px]">
            <Select value={month} onValueChange={(value) => handleFilterChange({ month: value })}>
              <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent>
                {monthOptions().map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[110px]">
            <Select value={year} onValueChange={(value) => handleFilterChange({ year: value })}>
              <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Year" /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 6 }, (_, i) => String(Number(getCurrentYear()) - i)).map((value) => (
                  <SelectItem key={value} value={value}>{value}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!branchModeSingle && (
            <div className="min-w-[180px]">
              <Select value={branchId} onValueChange={(value) => handleFilterChange({ branchId: value })}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {activeBranchOptions.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {currencyOptions.length > 1 && (
            <div className="min-w-[120px]">
              <Select value={currency || 'all'} onValueChange={(value) => handleFilterChange({ currency: value === 'all' ? '' : value })}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Currencies</SelectItem>
                  {currencyOptions.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button variant="outline" className="border-slate-200" onClick={() => load(month, year, branchId, currency)}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SummaryCard
          title="Current Balance"
          value={money(currentBalance, mainCurrency)}
          subtext={`Opening ${money(openingBalance, mainCurrency)} · Net ${money(netChange, mainCurrency)}`}
          trend={balanceIsUp ? <ArrowUpRight size={12} className="text-emerald-600" /> : <ArrowDownRight size={12} className="text-red-600" />}
          icon={Wallet}
        />
        <SummaryCard title="Opening Balance" value={money(summary.opening_balance || 0, mainCurrency)} subtext="Start of selected period" icon={CalendarDays} />
        <SummaryCard title="Total Income" value={money(summary.total_income || 0, mainCurrency)} subtext="Money earned in the selected period" icon={Banknote} />
        <SummaryCard title="Total Expenses" value={money(summary.total_expenses || 0, mainCurrency)} subtext="Money spent in the selected period" icon={CircleDollarSign} />
        <SummaryCard title="Net Change" value={money(summary.net_change || 0, mainCurrency)} subtext="Income minus expenses" icon={TrendingUp} />
        <SummaryCard title="Savings Amount" value={money(summary.savings_amount || 0, mainCurrency)} subtext="Positive cash reserve" icon={ShieldAlert} />
        <SummaryCard title="Expense Ratio" value={percent(summary.expense_ratio || 0)} subtext="Expenses as a percentage of income" icon={BadgeAlert} />
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5 flex flex-col items-center justify-center">
            <div className="flex items-center justify-between w-full mb-3">
              <p className="text-sm font-medium text-slate-500">Budget Health Score</p>
              <ToneBadge score={health.score} status={health.status || 'Good'} />
            </div>
            <HealthRing score={health.score} />
            <p className="text-xs text-slate-500 mt-3 text-center">Financial health based on savings, income growth, expense ratio and cash flow stability.</p>
          </CardContent>
        </Card>
      </div>

      {warnings.length ? (
        <div className="grid gap-3">
          {warnings.map((warning, index) => (
            <Card key={`${warning.title}-${index}`} className={`border rounded-2xl shadow-sm ${warning.type === 'danger' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${warning.type === 'danger' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                  <ShieldAlert size={18} />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${warning.type === 'danger' ? 'text-red-900' : 'text-amber-900'}`}>{warning.title}</p>
                  <p className={`text-sm mt-1 ${warning.type === 'danger' ? 'text-red-800' : 'text-amber-800'}`}>{warning.message}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Income vs Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="income" stroke="#059669" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Expense Categories</CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={categoryData} dataKey="amount" nameKey="category" outerRadius={100} innerRadius={50} paddingAngle={3}>
                      {categoryData.map((entry, index) => <Cell key={`category-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(value, name) => [money(value, mainCurrency), name]} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-slate-400 py-8 text-center">No expense data for this period.</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Allocation Breakdown</CardTitle>
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600 capitalize">
                {allocationMode}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {allocationData.length ? (
              <div className="space-y-4">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={allocationData} dataKey="amount" nameKey="name" outerRadius={100} innerRadius={50} paddingAngle={3}>
                        {allocationData.map((entry, index) => <Cell key={`allocation-${index}`} fill={COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(value, name) => [money(value, mainCurrency), name]} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-100">
                        <th className="py-2 pr-4 font-medium">Allocation Name</th>
                        <th className="py-2 pr-4 font-medium">Percentage</th>
                        <th className="py-2 pr-4 font-medium">Calculated Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocationData.map((item) => (
                        <tr key={item.id || item.name} className="border-b border-slate-50 last:border-0">
                          <td className="py-2 pr-4 text-slate-700">{item.name}</td>
                          <td className="py-2 pr-4 text-slate-700">{percent(item.percentage || 0)}</td>
                          <td className="py-2 pr-4 font-medium text-slate-900">{money(item.amount, item.currency || mainCurrency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="space-y-3 py-4">
                <p className="text-sm text-slate-400 text-center">No allocations configured yet.</p>
                <p className="text-xs text-slate-500 text-center">Set allocation rules to see your savings, rent, marketing, and other plan breakdowns.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Cash Flow Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cashFlowData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="income" fill="#059669" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="expenses" fill="#ef4444" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Financial Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {insights.map((item, index) => (
              <div key={index} className="flex gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <div className="mt-0.5 w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-emerald-700">
                  <Sparkles size={16} />
                </div>
                <p className="text-sm text-slate-700">{item}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Practical Advice</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {advice.map((item, index) => (
              <div key={index} className="flex gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <div className="mt-0.5 w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-blue-700">
                  <ChevronDown size={16} className="rotate-180" />
                </div>
                <p className="text-sm text-slate-700">{item}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Warnings</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">{warnings.length} item(s)</Badge>
        </CardHeader>
        <CardContent>
          {warnings.length ? (
            <div className="grid gap-3 md:grid-cols-2">
              {warnings.map((warning, index) => (
                <div key={`${warning.title}-${index}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{warning.title}</p>
                    <Badge className={warning.type === 'danger' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                      {warning.type}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{warning.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">No warnings for this period.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
